/* eslint-disable @typescript-eslint/no-explicit-any */

import { getLogger } from "@/lib/logging.ts"

interface RpcCallMessage<P = unknown> {
  id: string
  method: string
  params: P
}

interface RpcResponseMessage<R = unknown> {
  id: string
  result?: { data: R }
  error?: { message: string }
}

interface EventMessage<P = unknown> {
  method: string
  params: P
}

type RpcMessage = RpcCallMessage | RpcResponseMessage | EventMessage

interface RpcWindow extends Window {
  targetOrigin?: string
}

type MaybeRpcWindow = RpcWindow | (() => RpcWindow | null)

export type BrowserRpcClientApi<M, E> = {
  [K in keyof M]: M[K] extends (...args: infer A) => Promise<infer R> ? (...args: A) => Promise<R> : never
} & {
  on: <K extends keyof E>(event: K, listener: E[K] extends void ? () => void : (data: E[K]) => void) => () => void
  dispose: () => void
}

export type BrowserRpcServerApi<E> = {
  emit: <K extends keyof E>(event: K, params: E[K] extends void ? undefined : E[K]) => void
}

const log = getLogger("proto")

export function createServer<M, E>(win: MaybeRpcWindow, handler: { [K in keyof M]: M[K] extends (...args: any[]) => Promise<any> ? M[K] : never }) {

  const handleRpcCall = async (message: RpcCallMessage, sourceWindow: Window) => {
    const { id, method } = message
    const params = Array.isArray(message.params) ? message.params : [message.params]

    if (handler[method as keyof M] && typeof handler[method as keyof M] === "function") {
      try {
        const result = await (handler[method as keyof M] as any)(...params)
        const response: RpcResponseMessage = {
          id,
          result: { data: result },
        }

        sourceWindow.postMessage(response, "*")
      } catch (error: any) {
        const response: RpcResponseMessage = {
          id,
          error: { message: error.message },
        }

        sourceWindow.postMessage(response, "*")
      }
    } else {
      const response: RpcResponseMessage = {
        id,
        error: { message: `Method "${method}" not found.` },
      }

      sourceWindow.postMessage(response, "*")
    }
  }

  const emit = <K extends keyof E>(event: K, params: E[K] extends void ? undefined : E[K]) => {
    const message: EventMessage = {
      method: event as string,
      params,
    }

    const targetWindow = typeof win === "function" ? win() : win
    targetWindow?.postMessage(message, "*")
  }

  const handleMessage = (event: MessageEvent) => {
    const message = event.data as RpcMessage
    if (isRpcMessage(message) && isCallMessage(message)) {
      handleRpcCall(message as RpcCallMessage, event.source as Window).catch((e) => log.error("Failed to handle message", e))
    }
  }

  window.addEventListener("message", handleMessage)

  const dispose = () =>
    window.removeEventListener("message", handleMessage)

  return { emit, dispose }
}

export function createClient<M, E>(win: MaybeRpcWindow): BrowserRpcClientApi<M, E> {
  const pendingCalls = new Map<string, { resolve: (value: any) => void, reject: (reason?: any) => void }>()
  const eventListeners = new Map<string, ((...args: any[]) => void)[]>()

  const on = <K extends keyof E>(event: K, listener: E[K] extends void ? () => void : (data: E[K]) => void) => {
    const listeners = eventListeners.get(event as string) || []
    listeners.push(listener as any)
    eventListeners.set(event as string, listeners)

    return () => {
      eventListeners.set(event as string, listeners.filter(l => l !== listener))
    }
  }

  const handleRpcResponse = (message: RpcResponseMessage) => {
    const pending = pendingCalls.get(message.id)
    if (!pending) return

    if ("result" in message) {
      pending.resolve(message.result?.data)
    } else if ("error" in message) {
      pending.reject(new Error(message.error?.message))
    }

    pendingCalls.delete(message.id)
  }

  const handleEventEmit = (message: EventMessage) => {
    const listeners = eventListeners.get(message.method)
    listeners && listeners.forEach(listener => {
      listener(message.params)
    })
  }

  const sendMessage = (message: RpcCallMessage<unknown>) => {
    const targetWindow = typeof win === "function" ? win() : win
    targetWindow?.postMessage(message, "*")
  }

  const handleMessage = (event: MessageEvent) => {
    const message = event.data as RpcMessage
    if (!isRpcMessage(message)) {
      return
    }

    if (isResponseMessage(message)) {
      return handleRpcResponse(message)
    }

    if (isEventMessage(message)) {
      return handleEventEmit(message)
    }
  }

  const dispose = () => {
    window.removeEventListener("message", handleMessage)
    eventListeners.clear()
  }

  window.addEventListener("message", handleMessage)

  const rpcProxy = new Proxy({}, {
    get(_, prop, __) {
      if (prop === "on") {
        return on
      }

      if (prop === "dispose") {
        return dispose
      }

      return (...params: unknown[]) => {
        return new Promise((resolve, reject) => {
          const id = Math.random().toString(36).substring(2, 9)
          pendingCalls.set(id, { resolve, reject })

          const message: RpcCallMessage = {
            id,
            method: prop as string,
            params,
          }

          sendMessage(message)
        })
      }
    }
  })

  return rpcProxy as unknown as BrowserRpcClientApi<M, E>
}

function isCallMessage(arg: any): arg is RpcCallMessage {
  return typeof arg === "object" && arg !== null &&
    "id" in arg && typeof arg.id === "string" &&
    "method" in arg && typeof arg.method === "string" &&
    "params" in arg && !("result" in arg) && !("error" in arg)
}

function isResponseMessage(arg: any): arg is RpcResponseMessage {
  return typeof arg === "object" && arg !== null &&
    "id" in arg && typeof arg.id === "string" &&
    ("result" in arg || "error" in arg) &&
    !("result" in arg && "error" in arg)
}

function isEventMessage(arg: any): arg is EventMessage {
  return typeof arg === "object" && arg !== null &&
    "method" in arg && typeof arg.method === "string" &&
    "params" in arg && !("id" in arg)
}

export function isRpcMessage(arg: any): arg is RpcMessage {
  return isCallMessage(arg) || isResponseMessage(arg) || isEventMessage(arg)
}

export interface PreviewMethods {
  reload: () => Promise<void>
  navigate: (url: string) => Promise<void>
  togglePicker: (value: boolean) => Promise<void>
  takeScreenshot: () => Promise<string>
}

export interface PreviewEvents {
  unload: void
  errorOccurred: { error: Error }
  locationChanged: { url: string }
  pickerDismissed: void
  elementSelected: ElementSelectedData,
  devServerDetected: { kind: DevServer }
}

export type ElementSelectedData = {
  name: string
  path: string
  screenshotPngBase64: string
}

export enum DevServer {
  Vite = "vite",
  Webpack = "webpack",
  Unknown = "unknown"
}